/**
 * Render тексту вакансії з:
 *   - HTML-форматуванням (<b>, <i>, <u> з 1.0) через DOMPurify
 *   - Маскуванням контактів зірочками якщо юзер не unlock'нув:
 *       @username → @*****
 *       +380123… → ***********
 *       https://t.me/user → *********
 *       email → ********
 *
 * Логіка маскування — frontend-only (backend завжди шле raw текст для
 * сумісності, маскування — UX-фіча в Mini App).
 */

import { sanitizeHtml } from "@/lib/sanitize";

const TG_HANDLE = /@[A-Za-z][A-Za-z0-9_]{4,31}/g;
const PHONE = /(\+?\d[\d\-\s()]{7,}\d)/g;
const URL_RE =
  /(https?:\/\/[^\s<]+|t\.me\/[A-Za-z0-9_+/]+|tg:\/\/[A-Za-z0-9_=?&]+|wa\.me\/[^\s<]+|viber:\/\/[^\s<]+|forms\.gle\/[^\s<]+|bit\.ly\/[^\s<]+)/g;
const EMAIL = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
// Bare домени (example.com, my-site.com.ua) без protocol
const BARE_DOMAIN =
  /\b(?=[A-Za-z0-9-]*[A-Za-z])[A-Za-z0-9](?:[A-Za-z0-9-]{0,40}[A-Za-z0-9])?(?:\.[A-Za-z]{2,})+(?:\/[^\s<)>"'<]*)?/g;

// 1.0 footer що додавався парсером: блок з рядками
//   <b>Сфера: </b>...
//   <b>Джерело: </b>...
//   <b>Контакт: </b>...
// Threads-формат додатково містить:
//   <b>Посилання на вакансію в Threads: </b><a href=...>тиць</a>
//   <b>Інстаграм замовника: </b><a href=...>тиць</a>
// Прибираємо ВЕСЬ цей блок з тексту вакансії — інфо вже відображено
// окремо у hero-row (category badge + source). Контакти йдуть в окремий
// контактний блок з blur/unlock.
//
// 2026-05-23: ТІЛЬКИ структурні маркери що додає сам парсер у footer.
// Юзер скаржився: «AI summary згадав 25 000 грн, а ТЕКСТ ВАКАНСІЇ цього
// не показав» — корінь: stripFooter видаляв «Бюджет: 25 000» бо думав
// це footer, а насправді це КОРИСНИЙ ВМІСТ вакансії, не маркер.
//
// Залишаємо тільки те що парсери реально вставляють в footer:
// - Сфера / Джерело — добавляє Threads-парсер
// - Контакт — добавляє TG-чат-парсер
// - Посилання на вакансію / Інстаграм замовника — Threads-специфічні
//   labelled links
// - Детальніше про вакансію — Freelancehunt-специфічний label
//
// «Бюджет / Вартість / Регіон / Місто» — НЕ парсерні; вони пишуться
// в тіло вакансії автором і несуть цінну інфо.
const FOOTER_LABELS =
  "(?:Сфера|Джерело|Контакт|Посилання\\s+на\\s+вакансію|Інстаграм\\s+замовника|Детальніше\\s+про\\s+вакансію)";
const FOOTER_START = new RegExp(
  // Три формати footer-маркера що додає парсер:
  //   1) <b>Label …</b>                  (HTML bold)
  //   2) Label:                          (plain text + colon)
  //   3) <a href="...">Label …</a>       (Freelancehunt anchor-wraps-label)
  `(?:\\n+|^|\\s*)(?:<b>\\s*${FOOTER_LABELS}|${FOOTER_LABELS}\\s*:|<a[^>]*>\\s*${FOOTER_LABELS})[\\s\\S]*$`,
  "i",
);

export function stripFooter(text: string): string {
  return text.replace(FOOTER_START, "").trimEnd();
}

export interface VacancyFooterInfo {
  sphere?: string;
  source?: string;
  contact?: string;
  /** Threads-формат: «Посилання на вакансію в Threads: <a href=...>тиць</a>» */
  threadsUrl?: string;
  /** Threads-формат: «Інстаграм замовника: <a href=...>тиць</a>» */
  instagramUrl?: string;
}

/** Шукаємо лейбл або в `<b>Label:</b>` або в plain `Label:` форматі. */
function findLabelValue(text: string, label: string): string | undefined {
  // <b>Label:</b> ...
  const html = text.match(
    new RegExp(`<b>\\s*${label}\\s*:?\\s*<\\/b>\\s*([^\\n<]*)`, "i"),
  );
  if (html) return html[1].trim();
  // plain Label: ... (на початку рядка)
  const plain = text.match(
    new RegExp(`(?:^|\\n)\\s*${label}\\s*:\\s*([^\\n]*)`, "i"),
  );
  if (plain) return plain[1].trim();
  return undefined;
}

/** Витягує href із footer-маркера у трьох форматах:
 *    1) <b>Label …</b> <a href="URL"> — anchor після bold label
 *    2) Label: <a href="URL">          — anchor після plain label (двокрапка опц.)
 *    3) <a href="URL">Label …</a>      — anchor WRAPS label (Freelancehunt)
 */
function findLabelHref(text: string, label: string): string | undefined {
  // Спочатку (3) — anchor-wraps-label (Freelancehunt: «Детальніше про вакансію»)
  const wraps = text.match(
    new RegExp(`<a[^>]*\\bhref="([^"]+)"[^>]*>\\s*${label}`, "i"),
  );
  if (wraps) return wraps[1].trim();
  // (1) + (2) — label перед anchor; двокрапка опційна
  const after = text.match(
    new RegExp(
      `(?:<b>\\s*${label}[^<]*<\\/b>|${label}\\s*:?)\\s*<a[^>]*\\bhref="([^"]+)"`,
      "i",
    ),
  );
  return after ? after[1].trim() : undefined;
}

export function extractFooter(text: string): VacancyFooterInfo {
  const out: VacancyFooterInfo = {};
  out.sphere = findLabelValue(text, "Сфера");
  out.source = findLabelValue(text, "Джерело");
  out.contact = findLabelValue(text, "Контакт");
  // Threads-specific: href за лейблом
  out.threadsUrl = findLabelHref(text, "Посилання\\s+на\\s+вакансію");
  out.instagramUrl = findLabelHref(text, "Інстаграм\\s+замовника");
  // Якщо undefined → не повертаємо ключ (cleaner)
  if (!out.sphere) delete out.sphere;
  if (!out.source) delete out.source;
  if (!out.contact) delete out.contact;
  if (!out.threadsUrl) delete out.threadsUrl;
  if (!out.instagramUrl) delete out.instagramUrl;
  return out;
}

function maskString(s: string): string {
  // Порядок важен: email → URL → bare domain → phone → @username
  return s
    .replace(EMAIL, (m) => "*".repeat(Math.max(6, Math.min(m.length, 16))))
    .replace(URL_RE, (m) => "*".repeat(Math.max(8, Math.min(m.length, 16))))
    .replace(BARE_DOMAIN, (m) => "*".repeat(Math.max(8, Math.min(m.length, 16))))
    .replace(PHONE, (m) =>
      "*".repeat(Math.max(8, m.replace(/\D/g, "").length)),
    )
    .replace(TG_HANDLE, (m) => `@${"*".repeat(Math.max(5, m.length - 1))}`);
}

function maskHtmlText(html: string): string {
  // Bug fix 2026-05-20: раніше масковали тільки text inside тегів,
  // але <a href="https://...">текст</a> мав замаскований видимий
  // текст ALE href атрибут лишався → юзер тапав по зірочках і відкривав
  // реальне посилання.
  //
  // Fix: повністю замінюємо <a>...</a> на звіздочки (без href),
  // потім маскуємо plain-text URL/email/phones як раніше.
  let masked = html
    // 1) Видаляємо <a href=...>...</a> теги повністю — заміняємо на ***
    //    URL ВНУТРІШНЬОГО тексту зберігаємо лише як кількість зірочок,
    //    href ігнорується (юзер не може його дотиснутись).
    .replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, (_full, inner) => {
      const cleaned = inner.replace(/<[^>]+>/g, ""); // strip nested tags
      return "*".repeat(Math.max(8, Math.min(cleaned.length, 16)));
    });
  // 2) Маскуємо plain-text контакти всередині інших тегів (b/i/u/etc.)
  masked = masked.replace(/>([^<]+)</g, (_full, text: string) => `>${maskString(text)}<`);
  // 3) Якщо текст НА початку (до першого тегу) — також маскуємо
  masked = masked.replace(/^([^<]+)/, (_full, text: string) => maskString(text));
  return masked;
}

// Лінкіфікація «голих» URL у тексті: Telegram `url`-entity (видимий URL)
// приходить ПЛЕЙН-текстом, не в <a> → був некликабельний (юзер 2026-06-11:
// «десятки вакансій з посиланнями, по яких неможливо перейти»). Обгортаємо
// http(s)/t.me/tg посилання в <a target=_blank> — клік перехоплює
// openExternalOnAnchorClick і відкриває ЗОВНІ (браузер), не в webview.
// Група 1 = URL (http/t.me/tg). Група 2 = «межа» перед @ (початок рядка АБО
// один символ, що НЕ [\w@./]) — її треба зберегти у виводі. Група 3 = голий
// @username (TG-handle), робимо клікабельним → t.me/<handle>.
//
// ВАЖЛИВО (owner 2026-07-08, @kutnaksu на iOS < 16.4): БЕЗ lookbehind! Раніше
// стояв `(?<![\w@./])`, а lookbehind у regex підтримується лише з iOS Safari
// 16.4 — на старіших iOS ВЕСЬ модуль падав з «Invalid regular expression:
// invalid group specifier name» → апка взагалі не вантажилась (вічний спінер).
// Замінили lookbehind на явну capture-групу межі `(^|[^\w@./])`, яку емітимо
// назад як текст. Логіка та сама (email/вже-лінкований t.me/@user не чіпаємо),
// але працює на ВСІХ версіях Safari.
const URL_LINKIFY =
  /(https?:\/\/[^\s<]+|t\.me\/[^\s<]+|tg:\/\/[^\s<]+)|(^|[^\w@./])@([A-Za-z][A-Za-z0-9_]{4,31})\b/g;

function linkifyHtml(html: string): string {
  if (typeof document === "undefined") return html;
  const root = document.createElement("div");
  root.innerHTML = html;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    // Пропускаємо текст, що вже всередині <a> (Threads/text_link анкори).
    if ((node as Text).parentElement?.closest("a")) continue;
    URL_LINKIFY.lastIndex = 0;
    if (URL_LINKIFY.test(node.nodeValue || "")) targets.push(node as Text);
  }
  for (const text of targets) {
    const value = text.nodeValue || "";
    const frag = document.createDocumentFragment();
    let last = 0;
    URL_LINKIFY.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = URL_LINKIFY.exec(value))) {
      const start = m.index;
      if (start > last) frag.appendChild(document.createTextNode(value.slice(last, start)));
      let href: string;
      let display: string;
      let trailing = "";
      if (m[1]) {
        // URL
        let url = m[1];
        // Не захоплюємо хвостову пунктуацію (url. / url) / url, ...).
        const tm = url.match(/[.,);!?]+$/);
        if (tm) {
          trailing = tm[0];
          url = url.slice(0, -trailing.length);
        }
        href = url;
        display = url;
      } else {
        // m[2] = символ-межа перед @ (або порожній на початку рядка) — його
        // з'їв матч, тож повертаємо назад як текст ПЕРЕД лінком.
        const boundary = m[2] || "";
        if (boundary) frag.appendChild(document.createTextNode(boundary));
        // Голий @username → клікабельний t.me/<handle>
        const handle = m[3];
        href = `https://t.me/${handle}`;
        display = `@${handle}`;
      }
      const a = document.createElement("a");
      a.setAttribute("href", href);
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
      a.textContent = display;
      frag.appendChild(a);
      if (trailing) frag.appendChild(document.createTextNode(trailing));
      last = start + m[0].length;
    }
    if (last < value.length) frag.appendChild(document.createTextNode(value.slice(last)));
    text.parentNode?.replaceChild(frag, text);
  }
  return root.innerHTML;
}

export function renderVacancyHtml(
  rawText: string,
  contactsUnlocked: boolean,
): string {
  const stripped = stripFooter(rawText);
  const sanitized = sanitizeHtml(stripped);
  if (contactsUnlocked) return linkifyHtml(sanitized);
  return maskHtmlText(sanitized);
}
